import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient, } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { BULK_ACTION_MAX } from '@wit-support/admin-contracts';
import { Button, CopyButton, Dialog, DialogClose, DialogContent, DialogTitle, DropdownMenu, EmptyState, Heading, Popover, Skeleton, toast, } from '@wit-support/ui';
import { ChannelBadge, ConversationListItem, FilterBar, capitalize, hasAnyFilter, } from '@wit-support/ui-domain';
import { ContinuedFromBanner } from '../components/continued-from-banner.js';
import { StatusControl } from '../components/status-control.js';
import { ContactRail } from '../rail/contact-rail.js';
import { useMediaMatches } from '../lib/use-media-matches.js';
import { ArrowLeft, Inbox, Languages, Merge, MoreHorizontal, PanelRight, PanelRightClose, X, } from 'lucide-react';
import { ConversationMergedError, getConversation, listConversations, listInboxChannels, listMessages, listTags, listTopics, } from '../api.js';
import { MergeConversationDialog } from '../forms/merge-dialog.js';
import { useConversationsPerms } from '../perms.js';
import { ReplyComposer } from '../forms/reply-composer.js';
import { TranslatableThread } from '../translatable-thread.js';
import { BulkActionBar } from '../components/bulk-action-bar.js';
import { INBOX_FILTERS_KEY, INBOX_LAST_CONVERSATION_KEY, } from '../hooks/use-conversation-status-mutation.js';
import { bulkSelection, useBulkSelection } from '../hooks/use-bulk-selection.js';
import { CheckSquare } from 'lucide-react';
const STATUSES = [
    'open',
    'pending',
    'waiting',
    'resolved',
    'snoozed',
];
/** Shape check for persisted tag ids — the API rejects anything else with 400. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * How many tags the picker fetches for an EMPTY search. Generous on purpose:
 * with no query the same list also resolves the names of tag ids restored from
 * storage (the active-filter chips), and the vocabulary is operator-curated,
 * i.e. dozens, not thousands. A typed query narrows to the usual 50.
 */
const TAG_OPTIONS_LIMIT_BROWSE = 200;
const TAG_OPTIONS_LIMIT_SEARCH = 50;
/**
 * Read persisted inbox filters, falling back to the default open-only view on
 * any error / absence / shape mismatch. Best-effort + fail-soft: never throws,
 * so it is safe as a lazy useState initializer.
 *
 * The persisted value is validated field-by-field rather than cast blindly: a
 * malformed blob (hand-edited storage, stale data after a type change) must
 * degrade to the default view, never reach FilterBar / the API query with a
 * non-array where an array is expected (which would otherwise throw during
 * render — there is no error boundary — and re-crash on every reload).
 */
function readInboxFilters() {
    const fallback = { statuses: ['open'] };
    try {
        const raw = localStorage.getItem(INBOX_FILTERS_KEY);
        if (!raw)
            return fallback;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
            return fallback;
        const o = parsed;
        const stringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string') ? v : undefined;
        const clean = {};
        if (typeof o.q === 'string')
            clean.q = o.q;
        const channelAccountIds = stringArray(o.channelAccountIds);
        if (channelAccountIds)
            clean.channelAccountIds = channelAccountIds;
        const statuses = stringArray(o.statuses);
        if (statuses)
            clean.statuses = statuses;
        const topicKeys = stringArray(o.topicKeys);
        if (topicKeys)
            clean.topicKeys = topicKeys;
        if (typeof o.fromUtc === 'string')
            clean.fromUtc = o.fromUtc;
        if (typeof o.toUtcExclusive === 'string')
            clean.toUtcExclusive = o.toUtcExclusive;
        if (o.hasScheduledEmail === true)
            clean.hasScheduledEmail = true;
        if (o.awaitingOurReply === true)
            clean.awaitingOurReply = true;
        // Only the literal `true` — a stored `false` / `'true'` would either ask the
        // API for the complement or crash the strict enum; both degrade to «off».
        if (o.isWhale === true)
            clean.isWhale = true;
        if (o.hasChargeback === true)
            clean.hasChargeback = true;
        if (o.hasComment === true)
            clean.hasComment = true;
        // Element-wise: a single junk entry (hand-edited storage, a stale shape) is
        // dropped rather than taking the whole filter down — or worse, reaching the
        // API, whose `z.string().uuid()` would 400 the entire inbox on every reload.
        const tagIds = Array.isArray(o.tagIds)
            ? o.tagIds.filter((x) => typeof x === 'string' && UUID_RE.test(x))
            : [];
        if (tagIds.length > 0)
            clean.tagIds = tagIds;
        // Persisted so a reload mid-search keeps the «вернуть статусы» affordance and
        // still restores them when the search is cleared. Only meaningful alongside a
        // live `q` — dropped otherwise so a stale blob can't resurrect a status filter
        // out of nowhere.
        const suspended = stringArray(o.statusesSuspendedBySearch);
        if (suspended && clean.q) {
            clean.statusesSuspendedBySearch =
                suspended;
        }
        return clean;
    }
    catch {
        return fallback;
    }
}
export function InboxPage({ selectedConversationId, perms, }) {
    const [filters, setFilters] = useState(readInboxFilters);
    // Bulk-selection mode. Held in a MODULE-level store (not useState) so opening a
    // conversation — which remounts InboxPage via the sibling `/conversations/:id`
    // route — does NOT wipe the selection. EPHEMERAL: resets on explicit exit or a
    // full page reload, never persisted to localStorage.
    const { bulkMode, selectedIds } = useBulkSelection();
    const isWide = useMediaMatches('(min-width: 1280px)');
    const isNarrow = useMediaMatches('(max-width: 767px)');
    // Initialize from the breakpoint at mount so the FIRST paint is correct: the
    // rail (a full-screen Dialog drawer on mobile) defaults open only on wide
    // desktop. Initializing to `true` would mount the mobile drawer OPEN for one
    // frame on a fresh narrow mount — a flash + transient inert/focus jump before
    // the resize effect below closes it. The effect keeps it in sync on resize.
    const [railOpen, setRailOpen] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return true;
        return window.matchMedia('(min-width: 1280px)').matches;
    });
    // Default rail to closed on narrow viewports.
    useEffect(() => {
        setRailOpen(isWide);
    }, [isWide]);
    // On mobile the rail is a full-screen drawer; navigating to another conversation
    // (e.g. via «Другие диалоги» inside the rail) must close it so the chosen thread
    // is visible. No-op on desktop (isNarrow false) where the rail is a side column.
    useEffect(() => {
        if (isNarrow)
            setRailOpen(false);
    }, [selectedConversationId, isNarrow]);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    // Persist filters (incl. the `q` search) so they survive leaving + returning
    // to the route. Best-effort: a write failure never breaks the render.
    useEffect(() => {
        try {
            localStorage.setItem(INBOX_FILTERS_KEY, JSON.stringify(filters));
        }
        catch {
            /* best-effort persistence */
        }
    }, [filters]);
    // Remember the currently-open conversation so the sidebar «Чаты» link can deep
    // link back to it when the operator returns from another module (see
    // readInboxLastConversationId + AppShell). We deliberately do NOT redirect on
    // mount of the bare /conversations route: that would hijack the browser/mobile
    // Back button (Back from a thread to the list would bounce straight back into
    // the thread). The smart sidebar link restores on an intentional module return;
    // Back / the in-app back-to-list / blacklist all reach the bare list and show
    // it. Explicit closes (back-to-list, blacklist, status→closed) clear the key.
    useEffect(() => {
        if (selectedConversationId) {
            try {
                localStorage.setItem(INBOX_LAST_CONVERSATION_KEY, selectedConversationId);
            }
            catch {
                /* best-effort persistence */
            }
        }
    }, [selectedConversationId]);
    const channelsQuery = useQuery({
        queryKey: ['inbox-channels'],
        queryFn: () => listInboxChannels(),
        staleTime: 60_000,
    });
    const channelOptions = channelsQuery.data ?? [];
    const topicsQuery = useQuery({
        queryKey: ['topics'],
        queryFn: () => listTopics(),
        staleTime: 5 * 60_000,
    });
    const topicOptions = useMemo(() => (topicsQuery.data ?? []).map((t) => ({ key: t.key, name: t.name })), [topicsQuery.data]);
    // Tag picker options: the vocabulary narrowed by whatever the operator typed
    // into the picker's search box. Own key segment (`'inbox-filter'`) so it does
    // not share cache entries with the rail's `['tags', q]` (different limits),
    // while `invalidateQueries(['tags'])` after a tag write still reaches it.
    const [tagQuery, setTagQuery] = useState('');
    const tagsQuery = useQuery({
        queryKey: ['tags', 'inbox-filter', tagQuery],
        queryFn: () => listTags({
            ...(tagQuery ? { q: tagQuery } : {}),
            limit: tagQuery ? TAG_OPTIONS_LIMIT_SEARCH : TAG_OPTIONS_LIMIT_BROWSE,
        }),
        staleTime: 30_000,
        // Пока ищется новый запрос — прежняя выдача, а не пустой список: иначе
        // между нажатиями мелькает «Ничего не найдено».
        placeholderData: keepPreviousData,
    });
    const tagOptions = useMemo(() => (tagsQuery.data ?? []).map((t) => ({ id: t.id, name: t.name })), [tagsQuery.data]);
    // id → name for the active-filter chips, accumulated across every option list
    // this mount has seen: a tag picked before a narrowing search must keep its
    // name on the chip even though it is no longer among the current options.
    // The MEMORY of past lists is state written by an effect (a memo must stay
    // pure — a ref mutated inside one is invisible to React and leaks across
    // Strict-Mode double renders); the CURRENT options are merged in synchronously
    // below so a freshly loaded name never flashes «Тег недоступен» for a frame.
    const [rememberedTags, setRememberedTags] = useState(() => new Map());
    useEffect(() => {
        if (tagOptions.length === 0)
            return;
        setRememberedTags((prev) => {
            if (tagOptions.every((t) => prev.get(t.id) === t.name))
                return prev;
            const next = new Map(prev);
            for (const t of tagOptions)
                next.set(t.id, t.name);
            return next;
        });
    }, [tagOptions]);
    const knownTags = useMemo(() => {
        const merged = new Map(rememberedTags);
        for (const t of tagOptions)
            merged.set(t.id, t.name);
        return [...merged].map(([id, name]) => ({ id, name }));
    }, [rememberedTags, tagOptions]);
    // A persisted tag id the vocabulary no longer knows (the tag was deleted from
    // «Теги» while this filter sat in localStorage) would otherwise stay on as a
    // «Тег недоступен» chip that silently narrows the inbox to nothing. Checked
    // ONCE per mount against the full browse list (empty search, limit 200): the
    // ids at that point all come from storage, and after the drop the stored
    // filter no longer carries them, so a remount (opening a chat remounts this
    // page) does not toast again. A truncated browse list is NOT proof of
    // absence — a tag past the popularity cut-off is unknown here yet perfectly
    // real — so with ≥ limit rows the check is skipped rather than guessed.
    const unknownTagsChecked = useRef(false);
    const browseTags = tagQuery === '' && tagsQuery.isSuccess ? tagsQuery.data : null;
    useEffect(() => {
        if (unknownTagsChecked.current || browseTags == null)
            return;
        unknownTagsChecked.current = true;
        const ids = filters.tagIds ?? [];
        if (ids.length === 0 || browseTags.length >= TAG_OPTIONS_LIMIT_BROWSE)
            return;
        const known = new Set(browseTags.map((t) => t.id));
        const kept = ids.filter((id) => known.has(id));
        if (kept.length === ids.length)
            return;
        const { tagIds: _dropped, ...rest } = filters;
        setFilters(kept.length > 0 ? { ...rest, tagIds: kept } : rest);
        toast.info('Фильтр по удалённому тегу снят');
    }, [browseTags, filters]);
    const apiFilter = useMemo(() => {
        const f = { limit: 50 };
        if (filters.q)
            f.q = filters.q;
        if (filters.channelAccountIds?.length)
            f.channelAccountIds = filters.channelAccountIds;
        if (filters.statuses?.length)
            f.statuses = filters.statuses;
        if (filters.topicKeys?.length)
            f.topicKeys = filters.topicKeys;
        if (filters.fromUtc)
            f.from = filters.fromUtc;
        if (filters.toUtcExclusive)
            f.to = filters.toUtcExclusive;
        if (filters.hasScheduledEmail)
            f.hasScheduledEmail = true;
        if (filters.awaitingOurReply)
            f.awaitingOurReply = true;
        if (filters.isWhale)
            f.isWhale = true;
        if (filters.hasChargeback)
            f.hasChargeback = true;
        if (filters.hasComment)
            f.hasComment = true;
        if (filters.tagIds?.length)
            f.tagIds = filters.tagIds;
        return f;
    }, [filters]);
    const listQuery = useInfiniteQuery({
        queryKey: ['conversations', apiFilter],
        queryFn: ({ pageParam }) => listConversations({
            ...apiFilter,
            ...(pageParam ? { cursor: pageParam } : {}),
        }),
        initialPageParam: undefined,
        getNextPageParam: (last) => last.nextCursor ?? undefined,
        refetchInterval: 10_000,
    });
    const items = (listQuery.data?.pages ?? []).flatMap((p) => p.items);
    const selectedIdx = selectedConversationId
        ? items.findIndex((c) => c.id === selectedConversationId)
        : -1;
    const go = useCallback((idx) => {
        const next = items[idx];
        if (!next)
            return;
        void navigate({
            to: '/conversations/$conversationId',
            params: { conversationId: next.id },
        });
    }, [items, navigate]);
    // Bulk-selection helpers delegate to the shared module store (see
    // useBulkSelection). `selectAllLoaded` selects only the currently-rendered rows
    // (we don't auto-paginate the whole result set — loaded-only is acceptable).
    const toggleSelect = useCallback((id) => bulkSelection.toggle(id), []);
    const clearSelection = useCallback(() => bulkSelection.clear(), []);
    const exitBulkMode = useCallback(() => bulkSelection.exit(), []);
    const enterBulkMode = useCallback(() => bulkSelection.enter(), []);
    const selectAllLoaded = useCallback(() => {
        // Cap at the server's bulk limit — the bulk endpoint rejects > BULK_ACTION_MAX
        // ids, and the inbox can load past that across pages. Selecting the first N
        // keeps «select all» from producing a request that 400s on every action.
        bulkSelection.selectAll(items.slice(0, BULK_ACTION_MAX).map((c) => c.id));
    }, [items]);
    // Keyboard shortcuts: J/K navigate, [/] rail, / focus search, Esc closes rail.
    useEffect(() => {
        function onKeydown(e) {
            const target = e.target instanceof HTMLElement ? e.target : null;
            const inField = !!target &&
                (target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.isContentEditable ||
                    target.getAttribute('role') === 'searchbox');
            // While a modal dialog is open (bulk-action dialogs, the post-send status
            // popup, preview, …) the inbox shortcuts must not act underneath it — e.g.
            // J/K would navigate the thread out from under an open popup, leaving the
            // popup bound to a DIFFERENT conversation. Radix does not stopPropagation
            // on non-Esc keys, so guard here. Esc is exempt: the dialog closes it in
            // the capture phase (data-state is already 'closed' by now), so its own
            // `e.defaultPrevented` guard below handles it.
            if (e.key !== 'Escape' && document.querySelector('[role="dialog"][data-state="open"]')) {
                return;
            }
            if (e.key === '/' && !inField) {
                e.preventDefault();
                searchRef.current?.focus();
                return;
            }
            if (inField)
                return;
            if (e.key === 'j' || e.key === 'J') {
                e.preventDefault();
                go(selectedIdx >= 0 ? selectedIdx + 1 : 0);
                return;
            }
            if (e.key === 'k' || e.key === 'K') {
                e.preventDefault();
                go(selectedIdx > 0 ? selectedIdx - 1 : 0);
                return;
            }
            if (e.key === ']') {
                e.preventDefault();
                setRailOpen((v) => !v);
                return;
            }
            if (e.key === '[') {
                e.preventDefault();
                setRailOpen((v) => !v);
                return;
            }
            if (e.key === 'Escape') {
                // An open Radix dialog (bulk-action dialogs, post-send status popup,
                // preview, …) handles Esc in the CAPTURE phase — closing itself and
                // calling preventDefault() — BEFORE this bubble-phase listener runs. It
                // does not stopPropagation, so the event still reaches us, but by then
                // the dialog is already gone (a `[data-state="open"]` probe would miss
                // it). Keying off `defaultPrevented` is the reliable signal that a
                // dialog already consumed this Esc; without it, dismissing a dialog
                // would ALSO tear down bulk mode (wiping the whole selection) or
                // collapse the contact rail.
                if (e.defaultPrevented)
                    return;
                // Esc exits bulk mode first (if active), otherwise closes the rail.
                if (bulkMode) {
                    exitBulkMode();
                    return;
                }
                setRailOpen(false);
            }
        }
        document.addEventListener('keydown', onKeydown);
        return () => document.removeEventListener('keydown', onKeydown);
    }, [go, selectedIdx, bulkMode, exitBulkMode]);
    // Mobile master-detail: when a conversation is selected on narrow viewports
    // we replace the list with the thread (and vice-versa) to avoid a cramped
    // 160px list column. On >=md we keep both columns visible.
    const showThreadOnly = isNarrow && !!selectedConversationId;
    const showListOnly = isNarrow && !selectedConversationId;
    return (_jsxs("div", { className: [
            'h-[calc(100dvh-56px)] bg-[var(--color-surface-canvas)]',
            isNarrow
                ? 'flex flex-col'
                : 'grid grid-cols-[minmax(0,320px)_minmax(0,1fr)] md:grid-cols-[320px_minmax(0,1fr)]',
        ].join(' '), children: [!showThreadOnly ? (_jsx(LeftPane, { filters: filters, setFilters: setFilters, query: listQuery, items: items, selectedId: selectedConversationId, searchRef: searchRef, channelOptions: channelOptions, topicOptions: topicOptions, tagOptions: tagOptions, knownTags: knownTags, onTagSearch: setTagQuery, tagOptionsLoading: tagsQuery.isFetching, className: showListOnly ? 'flex-1' : '', bulkMode: bulkMode, selectedIds: selectedIds, onEnterBulkMode: enterBulkMode, onExitBulkMode: exitBulkMode, onToggleSelect: toggleSelect, onClearSelection: clearSelection, onSelectAllLoaded: selectAllLoaded, canBulk: perms.canReply, whaleSettingsHref: perms.canReadSettings ? '/settings' : undefined })) : null, !showListOnly ? (_jsx(ThreadArea, { conversationId: selectedConversationId, railOpen: railOpen, onToggleRail: () => setRailOpen((v) => !v), showBackToList: isNarrow, canToggleRail: true, isNarrow: isNarrow, perms: perms })) : null] }));
}
function LeftPane({ filters, setFilters, query, items, selectedId, searchRef, channelOptions, topicOptions, tagOptions, knownTags, onTagSearch, tagOptionsLoading, className, bulkMode, selectedIds, onEnterBulkMode, onExitBulkMode, onToggleSelect, onClearSelection, onSelectAllLoaded, canBulk, whaleSettingsHref, }) {
    // The same predicate the FilterBar's own «Reset» uses — one list of filter
    // keys, not a second copy that drifts the moment a filter is added.
    const hasFilters = hasAnyFilter(filters);
    // Bring the selected row into view whenever it changes — covers the restored
    // last-open chat as well as J/K keyboard navigation, provided the row is among
    // the loaded pages (we don't auto-paginate to find a row beyond the first
    // page). `block: 'nearest'` is a no-op when the row is already visible, so it
    // never yanks the list around unnecessarily. The stable useCallback ref fires
    // only when the element carrying it mounts/unmounts, so it does not re-run on
    // unrelated re-renders (e.g. the 10s refetch).
    const scrollSelectedIntoView = useCallback((node) => {
        node?.scrollIntoView({ block: 'nearest' });
    }, []);
    // Self-heal: if the operator loses bulk rights WHILE in bulk mode (a same-admin
    // role downgrade — the MeGate store reset only keys on admin.id, not role), the
    // entry button and the whole action bar (incl. its exit ✕) would otherwise
    // vanish and strand them in bulk mode. Tear it down cleanly instead.
    useEffect(() => {
        if (!canBulk && bulkMode)
            bulkSelection.exit();
    }, [canBulk, bulkMode]);
    return (_jsxs("aside", { className: [
            'flex min-h-0 flex-col border-[var(--color-border-default)] bg-[var(--color-surface-canvas)]',
            'border-b md:border-b-0 md:border-r',
            className ?? '',
        ].join(' '), children: [_jsxs(FilterBar, { value: filters, onChange: setFilters, className: "flex-col items-stretch gap-0 border-b border-[var(--color-border-subtle)] bg-transparent p-0", children: [_jsxs("div", { className: "flex flex-nowrap items-center gap-2 px-4 py-3", children: [_jsx(FilterBar.Search, { ref: searchRef, placeholder: "Search\u2026" }), _jsx(FilterBar.Filters, { statuses: STATUSES, channels: channelOptions, topicOptions: topicOptions, tagOptions: tagOptions, onTagSearch: onTagSearch, tagOptionsLoading: tagOptionsLoading, knownTags: knownTags }), canBulk ? (_jsxs(Button, { type: "button", variant: "secondary", size: "sm", onClick: () => (bulkMode ? onExitBulkMode() : onEnterBulkMode()), "aria-pressed": bulkMode, "aria-label": bulkMode ? 'Выйти из режима выбора' : 'Выбрать диалоги', title: bulkMode ? 'Выйти из режима выбора' : 'Выбрать диалоги', className: "relative shrink-0", children: [_jsx(CheckSquare, { className: "h-4 w-4", "aria-hidden": "true" }), bulkMode ? (_jsx("span", { className: "absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[var(--color-brand-bg)] ring-2 ring-[var(--color-surface-canvas)]", "aria-hidden": "true" })) : null] })) : null] }), _jsx(FilterBar.ActiveFilters, { channels: channelOptions, topicOptions: topicOptions, tagOptions: knownTags })] }), _jsxs("div", { className: "flex-1 overflow-auto", children: [query.isLoading ? (_jsx("div", { className: "flex flex-col gap-2 p-3", children: [0, 1, 2, 3].map((i) => (_jsx(Skeleton, { className: "h-16 w-full" }, i))) })) : query.isError ? (_jsx("p", { className: "p-4 text-sm text-[var(--color-state-danger-fg)]", children: "Failed to load conversations." })) : items.length === 0 ? (hasFilters ? (_jsxs(EmptyState, { children: [_jsx(Inbox, { className: "h-8 w-8 text-[var(--color-text-muted)]" }), _jsx("p", { className: "text-sm text-[var(--color-text-secondary)]", children: "No conversations match" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => setFilters({ statuses: ['open'] }), children: "Clear filters" })] })) : (_jsxs(EmptyState, { className: "p-10", children: [_jsx(Inbox, { className: "h-12 w-12 text-[var(--color-text-muted)]" }), _jsx("p", { className: "text-sm font-medium text-[var(--color-text-primary)]", children: "Your inbox is empty" }), _jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "Messages from channels will appear here." })] }))) : (_jsx("ul", { className: "flex flex-col", children: items.map((c) => (_jsx("li", { ref: c.id === selectedId ? scrollSelectedIntoView : undefined, children: _jsx(ConversationListItem, { conversation: c, selected: c.id === selectedId, selectable: bulkMode, checked: selectedIds.has(c.id), onToggleSelect: () => onToggleSelect(c.id), whaleSettingsHref: whaleSettingsHref, asChild: true, children: _jsx(Link, { to: "/conversations/$conversationId", params: { conversationId: c.id } }) }) }, c.id))) })), query.hasNextPage ? (_jsx("div", { className: "p-2", children: _jsx(Button, { variant: "ghost", size: "sm", className: "w-full", disabled: query.isFetchingNextPage, onClick: () => void query.fetchNextPage(), children: query.isFetchingNextPage ? 'Loading more…' : 'Load more' }) })) : null] }), bulkMode && canBulk ? (_jsx(BulkActionBar, { selectedIds: [...selectedIds], loadedCount: items.length, onSelectAll: onSelectAllLoaded, onClear: onClearSelection, onExit: onExitBulkMode })) : null] }));
}
export function ThreadArea({ conversationId, railOpen, onToggleRail, showBackToList, canToggleRail, isNarrow, perms, }) {
    // The header toggle that opens the mobile drawer. Because the drawer is a
    // controlled Dialog without a `Dialog.Trigger` (the toggle lives in a sibling
    // header), Radix has no trigger to restore focus to on close — so we restore
    // it ourselves via `onCloseAutoFocus`.
    const railToggleRef = useRef(null);
    if (!conversationId) {
        return (_jsx("section", { className: "flex h-full items-center justify-center bg-[var(--color-surface-canvas)]", children: _jsxs("div", { className: "flex flex-col items-center gap-3 text-center text-[var(--color-text-muted)]", children: [_jsx(Inbox, { className: "h-12 w-12" }), _jsx("p", { className: "text-sm", children: "Select a conversation to read" }), _jsxs("p", { className: "hidden text-xs md:block", children: ["Try", ' ', _jsx("kbd", { className: "rounded-[var(--radius-1)] border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-1.5 py-0.5 font-mono text-[10px]", children: "J" }), ' / ', _jsx("kbd", { className: "rounded-[var(--radius-1)] border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-1.5 py-0.5 font-mono text-[10px]", children: "K" }), ' to navigate'] })] }) }));
    }
    return (_jsxs("div", { className: `grid h-full min-h-0 ${railOpen && !isNarrow ? 'grid-cols-[minmax(0,1fr)_320px]' : 'grid-cols-[minmax(0,1fr)]'}`, children: [_jsx(ThreadView, { conversationId: conversationId, railOpen: railOpen, onToggleRail: onToggleRail, showBackToList: showBackToList, canToggleRail: canToggleRail, railToggleRef: railToggleRef, perms: perms }), railOpen && !isNarrow ? _jsx(ContactRail, { conversationId: conversationId, perms: perms }) : null, isNarrow ? (
            // Mobile: render the rail in the shared accessible Dialog primitive
            // (Radix) so focus is trapped + restored, the background is inert, and
            // Esc / overlay-click / the close button all flow through `railOpen`.
            // The header toggle button still opens it (sets railOpen=true).
            _jsx(Dialog, { open: railOpen, onOpenChange: (o) => (o ? undefined : onToggleRail()), children: _jsxs(DialogContent, { "aria-describedby": undefined, onCloseAutoFocus: (e) => {
                        // Restore focus to the header toggle on close (Radix can't, since
                        // the toggle isn't a Dialog.Trigger). Prevent Radix's default
                        // restore-to-body so keyboard users land back on the control.
                        const el = railToggleRef.current;
                        if (el) {
                            e.preventDefault();
                            el.focus();
                        }
                    }, className: "left-0 right-0 top-[56px] bottom-0 max-h-none w-full max-w-none translate-x-0 translate-y-0 gap-0 rounded-none border-0 p-0", 
                    // Классы `max-w-none`/`max-h-none` выше НИКОГДА не работали:
                    // `DialogContent` всегда проставляет `maxWidth`/`maxHeight`
                    // инлайном, а инлайн-стиль перебивает класс. Панель, задуманная во
                    // весь экран, зажималась в 32rem × 85dvh — на 520px это видимая
                    // поломка: справа оставалась полоса фона, снизу панель не доходила
                    // до края. `style` в `DialogContent` подмешивается ПОСЛЕДНИМ, так
                    // что это единственный способ снять зажим, не трогая кит.
                    //
                    // Токеном размера это не лечится и лечиться не должно: панель во
                    // весь экран — не диалог. Правильный дом для неё — `Sheet` из кита
                    // (`side="right"`), но это отдельная переверстка со своей
                    // проверкой; здесь снят только зажим.
                    style: { maxWidth: 'none', maxHeight: 'none' }, children: [_jsxs("div", { className: "flex items-center justify-between border-b border-[var(--color-border-default)] px-4 py-3", children: [_jsx(DialogTitle, { className: "text-sm font-medium text-[var(--color-text-primary)]", children: "\u0418\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044F \u043E \u0434\u0438\u0430\u043B\u043E\u0433\u0435" }), _jsx(DialogClose, { asChild: true, children: _jsx(Button, { type: "button", variant: "ghost", size: "sm", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043F\u0430\u043D\u0435\u043B\u044C", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) }) })] }), _jsx("div", { className: "min-h-0 flex-1 overflow-auto", children: _jsx(ContactRail, { conversationId: conversationId, perms: perms }) })] }) })) : null] }));
}
function ThreadView({ conversationId, railOpen, onToggleRail, showBackToList, canToggleRail, railToggleRef, perms, }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [mergeOpen, setMergeOpen] = useState(false);
    // false = show translation (default); true = show original
    const [showOriginalSubject, setShowOriginalSubject] = useState(false);
    // Reset the subject original/translation toggle when switching conversations so
    // the choice never leaks across threads.
    useEffect(() => setShowOriginalSubject(false), [conversationId]);
    const convoQuery = useQuery({
        queryKey: ['conversation', conversationId],
        queryFn: () => getConversation(conversationId),
        // Don't retry on a merge-redirect "error" — it's a control-flow signal.
        retry: (failureCount, err) => {
            if (err instanceof ConversationMergedError)
                return false;
            return failureCount < 2;
        },
    });
    const msgs = useQuery({
        queryKey: ['messages', conversationId],
        queryFn: () => listMessages(conversationId),
    });
    // Merging is owner/supervisor-only server-side (`conversation.merge`) — a tier
    // `role !== 'viewer'` cannot express, which is why the shell resolves the key.
    const canMerge = perms.canMerge;
    // Auto-navigate if the target conversation was merged away, and self-heal the
    // remembered "last open conversation" when its id can no longer be resolved.
    // Without the self-heal, a stored id that was deleted/blocked (generic 404) or
    // merged without a readable target would be restored on every return and
    // strand the operator on the error panel forever.
    useEffect(() => {
        const err = convoQuery.error;
        if (!err)
            return;
        if (err instanceof ConversationMergedError && err.targetId) {
            toast.success('Redirected — this conversation was merged');
            void navigate({
                to: '/conversations/$conversationId',
                params: { conversationId: err.targetId },
            });
            // The redirect re-points (and re-persists) to the live target — nothing to clear.
            return;
        }
        if (err instanceof ConversationMergedError) {
            // Merge detected but target id unreadable — can't navigate; prompt refresh.
            toast.error('Эта переписка была объединена — обновите страницу');
        }
        // Unresolvable target (generic load error, or merge without a target): drop
        // the remembered id so it is not auto-restored again on the next return.
        try {
            localStorage.removeItem(INBOX_LAST_CONVERSATION_KEY);
        }
        catch {
            /* best-effort */
        }
    }, [convoQuery.error, navigate]);
    const handleMerged = useCallback((targetId) => {
        setMergeOpen(false);
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        void queryClient.invalidateQueries({ queryKey: ['conversation', targetId] });
        void queryClient.invalidateQueries({ queryKey: ['messages', targetId] });
        void navigate({
            to: '/conversations/$conversationId',
            params: { conversationId: targetId },
        });
    }, [navigate, queryClient]);
    if (convoQuery.isError || !convoQuery.data) {
        if (convoQuery.isLoading) {
            return (_jsxs("section", { className: "flex min-h-0 flex-col gap-3 p-6", children: [_jsx(Skeleton, { className: "h-6 w-48" }), _jsx(Skeleton, { className: "h-32 w-full" })] }));
        }
        if (convoQuery.error instanceof ConversationMergedError) {
            // Effect above will navigate; render a brief placeholder.
            return (_jsx("section", { className: "p-6 text-[var(--color-text-muted)]", children: "Redirecting to merged conversation\u2026" }));
        }
        return (_jsx("section", { className: "p-6 text-[var(--color-state-danger-fg)]", children: "Failed to load conversation." }));
    }
    const conversation = convoQuery.data;
    const title = conversation.subject ||
        `${capitalize(conversation.channelKind)}${conversation.contact?.displayName
            ? ` · ${conversation.contact.displayName}`
            : conversation.contact?.primaryEmail
                ? ` · ${conversation.contact.primaryEmail}`
                : ''}`;
    // A distinct RU translation exists when the field is set and differs from the computed title.
    const hasTranslation = !!conversation.subjectTranslated && conversation.subjectTranslated !== title;
    // Default to the translation (showOriginalSubject=false → show translation).
    const shownTitle = hasTranslation && !showOriginalSubject ? conversation.subjectTranslated : title;
    return (_jsxs("section", { className: "flex min-h-0 flex-col bg-[var(--color-surface-canvas)]", children: [_jsxs("header", { 
                // In standalone PWA mode the sticky header must clear the device
                // notch/cutout, otherwise it slides under it. Add the safe-area inset
                // to the box height via top padding (0 on non-notched / browser tabs).
                className: "sticky top-0 z-10 flex h-14 items-center gap-2 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-3 pt-[env(safe-area-inset-top)] sm:gap-3 sm:px-4", children: [showBackToList ? (_jsx(Button, { variant: "ghost", size: "icon", className: "h-9 w-9", asChild: true, children: _jsx(Link, { to: "/conversations", "aria-label": "Back to conversations", onClick: () => {
                                // Explicit close: forget the remembered chat so it is not
                                // re-opened when InboxPage remounts on the bare route.
                                try {
                                    localStorage.removeItem(INBOX_LAST_CONVERSATION_KEY);
                                }
                                catch {
                                    /* best-effort */
                                }
                            }, children: _jsx(ArrowLeft, { className: "h-4 w-4", "aria-hidden": "true" }) }) })) : null, conversation.number != null ? (_jsxs(CopyButton, { value: String(conversation.number), variant: "ghost", size: "sm", className: "shrink-0 gap-1 px-1.5 font-mono text-xs text-[var(--color-text-muted)]", title: "\u0421\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043D\u043E\u043C\u0435\u0440 \u043E\u0431\u0440\u0430\u0449\u0435\u043D\u0438\u044F", ariaLabel: `Скопировать номер обращения ${conversation.number}`, successMessage: "\u041D\u043E\u043C\u0435\u0440 \u043E\u0431\u0440\u0430\u0449\u0435\u043D\u0438\u044F \u0441\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u043D", errorMessage: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0441\u043A\u043E\u043F\u0438\u0440\u043E\u0432\u0430\u0442\u044C", children: ["\u2116\u00A0", conversation.number] })) : null, _jsxs(Popover, { children: [_jsx(Popover.Trigger, { asChild: true, children: _jsx(Heading, { level: 2, size: "xs", className: "min-w-0 flex-1 cursor-pointer truncate text-left", title: shownTitle, children: shownTitle }) }), _jsx(Popover.Content, { className: "max-h-[60vh] max-w-[min(90vw,520px)] overflow-auto p-3 text-sm text-[var(--color-text-primary)]", children: hasTranslation ? (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("div", { children: [_jsx("p", { className: "mb-1 text-xs font-medium text-[var(--color-text-muted)]", children: "\u041F\u0435\u0440\u0435\u0432\u043E\u0434" }), _jsx("p", { className: "whitespace-pre-wrap break-words", children: conversation.subjectTranslated })] }), _jsxs("div", { children: [_jsx("p", { className: "mb-1 text-xs font-medium text-[var(--color-text-muted)]", children: "\u041E\u0440\u0438\u0433\u0438\u043D\u0430\u043B" }), _jsx("p", { className: "whitespace-pre-wrap break-words", children: title })] })] })) : (_jsx("span", { className: "whitespace-pre-wrap break-words", children: title })) })] }), hasTranslation ? (_jsxs(Button, { type: "button", variant: "ghost", size: "sm", "aria-label": showOriginalSubject ? 'Показать перевод' : 'Показать оригинал', onClick: () => setShowOriginalSubject((v) => !v), children: [_jsx(Languages, { className: "h-4 w-4", "aria-hidden": "true" }), showOriginalSubject ? 'Перевод' : 'Оригинал'] })) : null, _jsx(ChannelBadge, { kind: conversation.channelKind, size: "sm" }), _jsx(StatusControl, { conversationId: conversation.id, status: conversation.status, canEdit: perms.canReply }, conversation.id), canToggleRail ? (_jsx(Button, { ref: railToggleRef, type: "button", variant: "ghost", size: "sm", onClick: onToggleRail, "aria-label": railOpen ? 'Hide contact panel' : 'Show contact panel', "aria-pressed": railOpen, title: railOpen ? 'Hide contact panel ( ] )' : 'Show contact panel ( ] )', children: railOpen ? (_jsx(PanelRightClose, { className: "h-4 w-4", "aria-hidden": "true" })) : (_jsx(PanelRight, { className: "h-4 w-4", "aria-hidden": "true" })) })) : null, canMerge ? (_jsxs(DropdownMenu.Root, { children: [_jsx(DropdownMenu.Trigger, { asChild: true, children: _jsx(Button, { type: "button", variant: "ghost", size: "sm", "aria-label": "Conversation actions", title: "Conversation actions", children: _jsx(MoreHorizontal, { className: "h-4 w-4", "aria-hidden": "true" }) }) }), _jsx(DropdownMenu.Content, { align: "end", children: _jsxs(DropdownMenu.Item, { onSelect: () => setMergeOpen(true), children: [_jsx(Merge, { className: "h-4 w-4", "aria-hidden": "true" }), "Merge into\u2026"] }) })] })) : null] }), canMerge && mergeOpen ? (_jsx(MergeConversationDialog, { sourceConversationId: conversationId, onClose: () => setMergeOpen(false), onMerged: handleMerged })) : null, _jsx("div", { className: "min-h-0 flex-1 overflow-y-auto", children: _jsxs("div", { className: "mx-auto flex w-full max-w-[740px] flex-col gap-3 px-4 py-4", children: [conversation.parentConversationId ? (_jsx(ContinuedFromBanner, { parentId: conversation.parentConversationId, parentSubject: conversation.parentConversationSubject })) : null, _jsx(TranslatableThread
                        // Remount per conversation so the component's local translation
                        // state (translationMap / showTranslated) resets — it isn't keyed
                        // off conversationId internally and would otherwise grow unbounded
                        // across conversation switches.
                        , { conversationId: conversationId, messages: msgs.data?.items ?? [], isLoading: msgs.isLoading, className: "overflow-visible p-0" }, conversationId)] }) }), conversation.status !== 'closed' ? (_jsx(ReplyComposer
            // Key by conversation so the composer REMOUNTS on a thread switch
            // (param-only nav re-renders this same route in place). Without it the
            // local draft (text/subject/template), the per-conversation idempotency
            // refs, AND the open post-send status popup would leak onto the next
            // conversation — picking a status in a leaked popup would change the
            // WRONG conversation's status.
            , { conversationId: conversation.id, conversation: conversation, currentAdminDisplayName: perms.currentAdminDisplayName, canReply: perms.canReply }, conversation.id)) : null] }));
}
export function InboxRouteView() {
    const perms = useConversationsPerms();
    return _jsx(InboxPage, { perms: perms });
}
export function InboxConversationRouteView() {
    const params = useParams({ strict: false });
    const perms = useConversationsPerms();
    return params.conversationId ? (_jsx(InboxPage, { selectedConversationId: params.conversationId, perms: perms })) : (_jsx(InboxPage, { perms: perms }));
}
// Re-exports preserving this file's public API after the module split (task
// 21, FP-04) — several test files import these symbols from `inbox-page.js`.
export { ContinuedFromBanner } from '../components/continued-from-banner.js';
export { StatusControl } from '../components/status-control.js';
export { PortraitsPanel } from '../rail/portraits-panel.js';
export { ProductUsagePanel } from '../rail/product-usage-panel.js';
export { OtherConversationsPanel } from '../rail/other-conversations-panel.js';
export { PipelinesPanel, waitingForCustomerToast } from '../rail/pipelines-panel.js';
export { SubscriptionActionsPanel } from '../rail/subscription-actions-panel.js';
export { PipelineRunHistory } from '../rail/pipeline-run-history.js';
export { ClassificationBanner } from '../rail/classification-banner.js';
//# sourceMappingURL=inbox-page.js.map